
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { translate } from '../../../base/i18n';
import { MHTrackErrors } from '../../../base/lib-meet-hour';
import { Video } from '../../../base/media/components';
import { equals } from '../../../base/redux';
import { createLocalTracksForPreview } from '../../../base/tracks/functions';
import Spinner from '../../../base/ui/components/web/Spinner';
import { createVirtualBackgroundEffect } from '../../../stream-effects/virtual-background';
import { VIRTUAL_BACKGROUND_TYPE } from '../../constants';
import logger from '../../logger';

import { useStyles } from './styles';

/**
 * Track creation failures that describe the user's own setup (no camera, permission not granted) rather than a
 * malfunction. They are shown in the preview placeholder instead of being logged as errors.
 */
const EXPECTED_PREVIEW_ERRORS = new Set([
    MHTrackErrors.NOT_FOUND,
    MHTrackErrors.PERMISSION_DENIED
]);

type Props = {

    /**
     * The currently selected video input device ID.
     */
    selectedVideoInputId: ?string,

    /**
     * Callback when preview finishes loading.
     */
    loadedPreview: ?(loaded: boolean) => void,

    /**
     * The local selection state from the dialog (decoupled from Redux).
     * This represents what the user is currently selecting, NOT the applied background.
     */
    localSelection: {
        backgroundType: string,
        selectedThumbnail: string,
        virtualSource: string,
        blurValue: number
    }
};

/**
 * VirtualBackgroundPreview - Shows a live preview of the selected virtual background.
 *
 * This component receives the local selection from the parent dialog and applies
 * the effect to a preview track. It is completely decoupled from the global Redux state.
 *
 * @param {Props} props - Component props.
 * @returns {React$Element}
 */
function VirtualBackgroundPreview({ selectedVideoInputId, loadedPreview, localSelection }: Props) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { classes: styles } = useStyles();
    const [ mhTrack, setMhTrack ] = useState(null);
    const [ loading, setLoading ] = useState(true);
    const [ previewError, setPreviewError ] = useState(null);

    // Refs for tracking current values and cleanup
    const trackRef = useRef(null);
    const prevSelectionRef = useRef(localSelection);

    /**
     * Creates the local video track for preview.
     */
    useEffect(() => {
        let canceled = false;

        setLoading(true);

        createLocalTracksForPreview({
            devices: [ 'video' ],
            facingMode: 'user',
            cameraDeviceId: selectedVideoInputId || undefined
        }, APP.store)
            .then(tracks => {
                if (canceled) {
                    tracks.forEach(track => track.dispose());

                    return;
                }

                if (tracks.length > 0) {
                    const localTrack = tracks[0];

                    trackRef.current = localTrack;
                    setMhTrack(localTrack);
                    setLoading(false);
                    loadedPreview?.(true);
                } else {
                    setLoading(false);
                    logger.warn('No video tracks were created for preview');
                }
            })
            .catch(error => {
                if (canceled) {
                    return;
                }

                // A denied permission or an absent camera is a normal user state, not a fault: it is reported in
                // the placeholder below. Only genuinely unexpected failures deserve an error in the logs.
                if (EXPECTED_PREVIEW_ERRORS.has(error?.name)) {
                    logger.warn(`No camera preview available: ${error.name}`);
                } else {
                    logger.error('Failed to create local video track for preview:', error);
                }

                setPreviewError(error);
                setLoading(false);
                loadedPreview?.(false);
            });

        // Cleanup function
        return () => {
            canceled = true;

            const currentTrack = trackRef.current;

            if (currentTrack) {
                try {
                    if (currentTrack.setEffect) {
                        currentTrack.setEffect(undefined);
                    }
                } catch (e) {
                    logger.warn('Error removing effect during unmount:', e);
                }
                currentTrack.dispose();
                trackRef.current = null;
            }

            setMhTrack(null);
        };
    }, [ selectedVideoInputId, loadedPreview ]);

    /**
     * Applies the virtual background effect based on localSelection changes.
     */
    const applyEffect = useCallback(async () => {
        if (!mhTrack) {
            return;
        }

        try {
            const { backgroundType, virtualSource, blurValue } = localSelection;

            if (backgroundType === VIRTUAL_BACKGROUND_TYPE.NONE) {
                // Remove any existing effect
                await mhTrack.setEffect(undefined);
            } else {
                // Create and apply the effect
                const effectOptions = {
                    backgroundEffectEnabled: true,
                    backgroundType,
                    virtualSource,
                    blurValue,
                    selectedThumbnail: localSelection.selectedThumbnail
                };

                const effect = await createVirtualBackgroundEffect(effectOptions, dispatch);

                await mhTrack.setEffect(effect);
            }
        } catch (error) {
            logger.error('Error applying virtual background effect to preview:', error);
        }
    }, [ mhTrack, localSelection, dispatch ]);

    /**
     * Watch for localSelection changes and apply effect.
     */
    useEffect(() => {
        // Only apply if selection has actually changed
        if (mhTrack && !equals(prevSelectionRef.current, localSelection)) {
            applyEffect();
            prevSelectionRef.current = localSelection;
        }
    }, [ localSelection, mhTrack, applyEffect ]);

    // ============ RENDER ============

    if (loading) {
        return (
            <div className = { styles.previewLoader }>
                <Spinner />
                <p className = { styles.loadingText }>Loading camera preview...</p>
            </div>
        );
    }

    if (!mhTrack) {
        // Tell the user which of the two very different problems they have, instead of always blaming permissions.
        const hint = previewError?.name === MHTrackErrors.NOT_FOUND
            ? t('dialog.cameraNotFoundError')
            : t('dialog.cameraPermissionDeniedError');

        return (
            <div className = { styles.previewPlaceholder }>
                <div className = { styles.placeholderContent }>
                    <p>No camera preview available</p>
                    <p className = { styles.placeholderHint }>{ hint }</p>
                </div>
            </div>
        );
    }

    return (
        <div className = { styles.preview }>
            <Video
                autoPlay = { true }
                className = { styles.previewVideo }
                muted = { true }
                playsInline = { true }
                videoTrack = {{ mhTrack }} />
        </div>
    );
}

export default translate(VirtualBackgroundPreview);
